import React from "react";
import { useSearchParams } from "react-router";

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AreaAuditTab } from "@/pages/tina/audits/area";
import { ArtistAuditTab } from "@/pages/tina/audits/artist";
import { VenueAuditTab } from "@/pages/tina/audits/venue";
import { usePersistedState } from "@/pages/tina/hooks/use-persisted-state";

export type AuditsTab = "venue" | "artist" | "area";

interface TabEntry {
  value: AuditsTab;
  label: string;
  hint: string;
}

const TABS: readonly TabEntry[] = [
  {
    value: "venue",
    label: "Venue",
    hint: "Venue binds the daily audit thinks Tina got wrong.",
  },
  {
    value: "artist",
    label: "Artist",
    hint: "Artist records the daily audit thinks are one act twice.",
  },
  {
    value: "area",
    label: "Area",
    hint: "Areas and venue placements the nightly area step could not settle on its own.",
  },
];

export function isAuditsTab(value: string | null): value is AuditsTab {
  return TABS.some((entry) => entry.value === value);
}

/**
 * `/tina/audits` — every daily audit's review queue, one tab each.
 *
 * The venue audit had this page to itself as `/tina/venue-audit`. The artist audit is the
 * second daily read-only audit with the same shape — findings a curator reads and
 * dismisses, nothing else — and a sidebar entry per audit does not scale: the zero-event
 * artist queue the spec defers would be a third. The two share every piece of chrome a
 * curator actually touches, so they share a page. Spec section 9.1.
 *
 * **Dismissal is the only action on either tab.** Nothing here writes to a venue, an
 * artist or an event; fixing a finding means opening the record in Mood, which every link
 * in a Finding column does. Spec section 9.2 and invariant 10.1.
 *
 * The area audit joined as the third tab on 2026-11-01, taking over the Issues tab of
 * `/areas`: the venue audit job's nightly area step files its findings the same way.
 *
 * The tab is persisted like the Rules page's, and an initial `?tab=` wins over the
 * persisted value, so `/tina/venue-audit` can redirect a bookmark onto the right tab and
 * a Discord message can link straight to the artist queue.
 */
export const AuditsList = () => {
  const [searchParams] = useSearchParams();
  const requested = searchParams.get("tab");

  const [tab, setTab] = usePersistedState<AuditsTab>(
    "tina-audits:tab",
    "venue",
  );

  // A `?tab=` on the URL is a one-shot instruction, not state: apply it once per
  // distinct value so a curator can still click away from it afterwards.
  const appliedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!isAuditsTab(requested) || appliedRef.current === requested) return;
    appliedRef.current = requested;
    setTab(requested);
  }, [requested, setTab]);

  // Narrowed on read: the persisted value can be a tab an older build wrote.
  const active = TABS.find((entry) => entry.value === tab) ?? TABS[0];

  return (
    <div className="flex flex-col gap-4 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Audits</h1>
        <p className="max-w-3xl text-sm text-muted-foreground">
          What the daily read-only audits found. Nothing on this page changes a
          record — fix one by opening it in Mood.{" "}
          <strong className="font-medium text-foreground">
            Dismissing hides a finding for good
          </strong>{" "}
          until the records involved change.
        </p>
      </div>
      <div className="flex flex-col gap-1">
        <Tabs
          value={active.value}
          onValueChange={(value) => {
            if (isAuditsTab(value)) setTab(value);
          }}
        >
          <TabsList>
            {TABS.map((entry) => (
              <TabsTrigger key={entry.value} value={entry.value}>
                {entry.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <p className="text-xs text-muted-foreground">{active.hint}</p>
      </div>
      {/* One tab mounted at a time: each tab owns a `useTable`, which is a hook, so the
          tabs cannot be selected inside one component without fetching every queue on
          every render. Same reason the Rules page does it this way. */}
      {active.value === "venue" ? (
        <VenueAuditTab />
      ) : active.value === "artist" ? (
        <ArtistAuditTab />
      ) : (
        <AreaAuditTab />
      )}
    </div>
  );
};
